import { severityOf } from '../insights/insightsModel';
// Names come from insightsModel.sessionDisplayName (tester's name, "You" for
// owner previews, else "Anonymous tester"); only the number label is here.

/**
 * Session review model (v3 phase 5b, 5d, 5f): pure helpers that turn a stored
 * session into what Watch and Read draw. Nothing here invents data:
 *
 * - lines come from the stored word timings (`session.transcript`, AssemblyAI
 *   words with `start` / `end` in ms); no words, no lines
 * - task regions come from the task ticks (`taskResults[].startedAt` /
 *   `completedAt`), placed against `session.recordingStartedAt` when it exists
 *   and otherwise against `session.startedAt` (marked `estimated`, a few
 *   seconds out)
 * - tag marks come only from findings that carry a time (`t`, seconds from
 *   the recording start, written by the timed-quotes step, PR #278); findings
 *   without one are listed but never placed
 * - silences come from the gaps between the stored words
 */

/* ---------- time ---------- */

/** Firestore Timestamp, Date, ISO string or ms number -> ms, or null. */
export function toMs(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'object' && typeof v.seconds === 'number') return v.seconds * 1000 + Math.round((v.nanoseconds || 0) / 1e6);
  const d = new Date(v).getTime();
  return Number.isNaN(d) ? null : d;
}

/** 83000 -> "01:23"; an hour or more -> "1:02:03". */
export function clock(ms) {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return '--:--';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Seconds (a number, as PR #278 stores `t` / `tEnd`, or "mm:ss") -> ms, or null. */
export function secondsToMs(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number') return Number.isFinite(raw) && raw >= 0 ? Math.round(raw * 1000) : null;
  if (typeof raw === 'string') {
    const m = raw.match(/^(?:(\d+):)?(\d{1,2}):(\d{2})$/);
    if (m) return ((Number(m[1] || 0) * 60 + Number(m[2])) * 60 + Number(m[3])) * 1000;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 1000) : null;
  }
  return null;
}

/** A finding's moment in ms from the start of the recording, or null. */
export function findingTime(item) {
  if (!item || typeof item !== 'object') return null;
  return secondsToMs(item.t);
}

/* ---------- words and lines ---------- */

/** The stored words, cleaned: only entries with text and numeric times. */
export function getWords(session) {
  const raw = session && Array.isArray(session.transcript) ? session.transcript : [];
  const out = [];
  for (const w of raw) {
    if (!w || typeof w.text !== 'string') continue;
    const start = Number(w.start);
    const end = Number(w.end);
    if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
    out.push({ text: w.text, start, end: Math.max(end, start) });
  }
  return out;
}

const SENTENCE_END = /[.!?…]["')\]]?$/;
const SOFT_BREAK = /[,;:—–-]$/;
const PAUSE_BREAK_MS = 1500;
const MAX_WORDS = 32;
const MIN_WORDS = 3;
const LONG_LINE = 12;
const SENTENCE_PAUSE_MS = 700;

/**
 * Sentence-shaped lines from the words. A line ends at a sentence end (once it
 * has a few words), at a long pause, at a task start, or when it runs long (at
 * a comma if there is one). Each line keeps the index range of its words so
 * the highlight can find the spoken word without searching every line.
 */
export function buildLines(words, taskStarts = []) {
  const lines = [];
  if (!words.length) return lines;
  const starts = [...taskStarts].filter((t) => Number.isFinite(t)).sort((a, b) => a - b);
  let from = 0;
  const close = (to) => {
    const ws = words.slice(from, to + 1);
    lines.push({
      i: lines.length,
      from,
      to,
      start: ws[0].start,
      end: ws[ws.length - 1].end,
      text: ws.map((w) => w.text).join(' '),
    });
    from = to + 1;
  };
  for (let k = 0; k < words.length; k++) {
    const w = words[k];
    const next = words[k + 1];
    const count = k - from + 1;
    if (!next) { close(k); break; }
    const gap = next.start - w.end;
    const crossesTask = starts.some((t) => w.end <= t && next.start >= t) ||
      starts.some((t) => t > w.start && t <= next.start);
    if (crossesTask) { close(k); continue; }
    if (gap >= PAUSE_BREAK_MS) { close(k); continue; }
    // A sentence end closes the line once it is long enough, or when a short
    // pause follows it (quick sentences stay together, as people speak).
    if (SENTENCE_END.test(w.text) && count >= MIN_WORDS && (count >= LONG_LINE || gap >= SENTENCE_PAUSE_MS)) { close(k); continue; }
    if (count >= MAX_WORDS) { close(k); continue; }
    if (count >= MAX_WORDS - 10 && SOFT_BREAK.test(w.text)) { close(k); continue; }
  }
  return lines;
}

/** Index of the last item whose `start` is at or before ms (binary search), or -1. */
export function indexAt(items, ms, key = 'start') {
  let lo = 0;
  let hi = items.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (items[mid][key] <= ms) { found = mid; lo = mid + 1; } else { hi = mid - 1; }
  }
  return found;
}

/** Gaps between words long enough to skip ("Skip silence"). Includes the
 *  quiet before the first word and after the last one. */
export function findSilences(words, durationMs, minGapMs = 2000) {
  const out = [];
  if (!words.length) return out;
  if (words[0].start >= minGapMs) out.push({ start: 0, end: words[0].start });
  for (let k = 0; k < words.length - 1; k++) {
    const a = words[k].end;
    const b = words[k + 1].start;
    if (b - a >= minGapMs) out.push({ start: a, end: b });
  }
  const last = words[words.length - 1].end;
  if (Number.isFinite(durationMs) && durationMs - last >= minGapMs) out.push({ start: last, end: durationMs });
  return out;
}

/** Speech lane: merged spans where the tester was speaking (from the words). */
export function speechSpans(words, joinMs = 600) {
  const out = [];
  for (const w of words) {
    const last = out[out.length - 1];
    if (last && w.start - last.end <= joinMs) last.end = Math.max(last.end, w.end);
    else out.push({ start: w.start, end: w.end });
  }
  return out;
}

/* ---------- tasks ---------- */

/**
 * Task regions on the recording. Placed against `recordingStartedAt` (exact)
 * when it is stored, else `startedAt` (estimated). Returns [] when no task has
 * a start time.
 */
export function taskRegions(session, tasks = []) {
  const results = Array.isArray(session?.taskResults) ? session.taskResults : [];
  const exactBase = toMs(session?.recordingStartedAt);
  const base = exactBase ?? toMs(session?.startedAt);
  if (base === null) return [];
  const out = [];
  results.forEach((r, idx) => {
    const s = toMs(r.startedAt);
    if (s === null) return;
    let e = toMs(r.completedAt);
    if (e === null && Number.isFinite(Number(r.timeSpent))) e = s + Number(r.timeSpent) * 1000;
    const order = tasks.findIndex((t) => t.id === r.taskId);
    const def = order >= 0 ? tasks[order] : null;
    out.push({
      key: r.taskId || `t${idx}`,
      n: order >= 0 ? order + 1 : idx + 1,
      title: def?.title || r.taskTitle || `Task ${idx + 1}`,
      description: def?.instructions || def?.description || '',
      status: r.status === 'done' ? 'done' : (r.status ? 'stuck' : 'open'),
      timeSpent: Number(r.timeSpent) || null,
      start: Math.max(0, s - base),
      end: e === null ? null : Math.max(0, e - base),
      estimated: exactBase === null,
    });
  });
  out.sort((a, b) => a.start - b.start);
  // Close open regions at the next task's start.
  out.forEach((r, i) => {
    if (r.end === null || r.end < r.start) r.end = out[i + 1] ? out[i + 1].start : null;
  });
  return out;
}

/** "2 of 2" style task count for a session, or null when the test has no tasks. */
export function tasksDone(session, tasks = []) {
  const results = Array.isArray(session?.taskResults) ? session.taskResults : [];
  const total = Math.max(tasks.length, results.length);
  if (!total) return null;
  const done = results.filter((r) => r.status === 'done').length;
  return { done, total };
}

/* ---------- findings ---------- */

const KIND_WORDS = {
  issue: 'Issue',
  confusion: 'Confusion',
  good: 'Works well',
  request: 'Request',
};

export function kindWord(kind) {
  return KIND_WORDS[kind] || 'Note';
}


/** Severity as Critical, Major or Minor, from THE mapping in insightsModel
 *  (old high/medium/low read too); null when the finding has none. */
export function severityWord(sev) {
  if (!sev || typeof sev !== 'string') return null;
  return severityOf(sev).label;
}

/**
 * Every finding of a session's analysis, in one list:
 * `{ id, kind, title, quote, severity, recommendation, t, tEnd, taskId }`
 * where `t` / `tEnd` are ms from the start of the recording, or null when the
 * analysis has no time for it. Reads the per-session lists (friction_points,
 * confusion_moments, positive_reactions, feature_requests), whose findings
 * carry `t`, `tEnd` (seconds), `tScore`, `sessionId` and `taskId` once the
 * timed-quotes step (PR #278) has run. `analysis.quotes[]` mirrors those
 * findings ({ kind: issue|confusion|worked|request, index, t, ... }); it only
 * fills a time a list item is missing, so nothing is counted twice.
 */
const LISTS = [
  ['friction_points', 'issue'],
  ['confusion_moments', 'confusion'],
  ['positive_reactions', 'good'],
  ['feature_requests', 'request'],
];
const QUOTE_KINDS = { issue: 'issue', confusion: 'confusion', worked: 'good', request: 'request' };

export function getFindings(session) {
  const a = session?.analysis;
  if (!a || typeof a !== 'object') return [];
  const out = [];
  const byKey = new Map();
  for (const [list, kind] of LISTS) {
    (Array.isArray(a[list]) ? a[list] : []).forEach((item, i) => {
      if (!item) return;
      const obj = typeof item === 'string' ? { description: item } : item;
      const f = {
        id: `${kind}-${i}`,
        kind,
        title: obj.description || obj.title || '',
        quote: obj.quote || '',
        severity: obj.severity ? severityWord(obj.severity) : null,
        recommendation: obj.recommendation || '',
        t: findingTime(obj),
        tEnd: secondsToMs(obj.tEnd),
        taskId: obj.taskId || null,
      };
      out.push(f);
      byKey.set(`${kind}-${i}`, f);
    });
  }
  (Array.isArray(a.quotes) ? a.quotes : []).forEach((q) => {
    if (!q || (q.sessionId && session.id && q.sessionId !== session.id)) return;
    const kind = QUOTE_KINDS[q.kind];
    const f = kind && Number.isInteger(q.index) ? byKey.get(`${kind}-${q.index}`) : null;
    if (f && f.t === null) {
      f.t = secondsToMs(q.t);
      f.tEnd = secondsToMs(q.tEnd);
      f.taskId = f.taskId || q.taskId || null;
    }
  });
  return out;
}

/** The findings that carry a time, sorted, for marks and line tags. */
export function timedFindings(findings) {
  return findings.filter((f) => f.t !== null && f.kind !== 'request').sort((a, b) => a.t - b.t);
}


/** The lead quote for Read: the most severe issue's quote, then any confusion
 *  quote, then any quote. Exact words only; null when there is none. */
export function leadQuote(findings) {
  const withQuote = findings.filter((f) => f.quote && f.quote.trim().length > 0);
  if (!withQuote.length) return null;
  const rank = (f) => (f.kind === 'issue' ? 0 : f.kind === 'confusion' ? 1 : 2) * 10 +
    (f.severity ? severityOf(f.severity).rank : 5);
  return [...withQuote].sort((x, y) => rank(x) - rank(y))[0];
}

/** Place each timed finding under the line it was said in. */
export function tagsByLine(lines, timed) {
  const map = new Map();
  if (!lines.length) return map;
  for (const f of timed) {
    let li = indexAt(lines, f.t);
    if (li < 0) li = 0;
    if (!map.has(li)) map.set(li, []);
    map.get(li).push(f);
  }
  return map;
}

/* ---------- session facts ---------- */

const DEVICE_WORDS = { desktop: 'Computer', laptop: 'Computer', mobile: 'Phone', phone: 'Phone', tablet: 'Tablet' };

function uaFacts(ua = '') {
  const isTablet = /iPad|Tablet/i.test(ua);
  const isMobile = /Mobile|Android|iPhone/i.test(ua) && !isTablet;
  let browser = null;
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua)) browser = 'Safari';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  let os = null;
  if (/Windows NT/.test(ua)) os = 'Windows';
  else if (/iPhone|iPad/.test(ua)) os = 'iOS';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Linux/.test(ua)) os = 'Linux';
  return { device: ua ? (isTablet ? 'Tablet' : isMobile ? 'Phone' : 'Computer') : null, browser, os };
}

/** "Computer · Chrome · macOS" from structured metadata, else the user agent; null when unknown. */
export function deviceLine(session) {
  const m = session?.metadata || {};
  const ua = uaFacts(m.userAgent || '');
  const device = DEVICE_WORDS[String(m.deviceType || '').toLowerCase()] || ua.device;
  const browser = m.browserName || ua.browser;
  const os = m.osName || ua.os;
  const parts = [device, browser, os].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/** Short device word for a row: "Computer, Chrome". */
export function deviceShort(session) {
  const line = deviceLine(session);
  if (!line) return null;
  return line.split(' · ').slice(0, 2).join(', ');
}

/** "Tue 20 Oct, 21:09" (British, 24h). */
export function whenLine(v) {
  const ms = toMs(v);
  if (ms === null) return '';
  const d = new Date(ms);
  const day = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day.replace(',', '')}, ${time}`;
}

/** Length of the session in ms from startedAt to completedAt, or null. */
export function sessionLengthMs(session) {
  const a = toMs(session?.startedAt);
  const b = toMs(session?.completedAt);
  if (a === null || b === null || b < a) return null;
  return b - a;
}

/** What was recorded, in words. */
export function recordedLine(session) {
  if (session?.videoUrl && session?.audioUrl) return 'Screen and voice';
  if (session?.videoUrl) return 'Screen only';
  if (session?.audioUrl) return 'Voice only';
  return null;
}

/**
 * The transcript's state, one of:
 * 'ready' (words to show), 'text' (plain text but no word timings),
 * 'arriving' (audio uploaded, transcript not back yet), 'failed',
 * 'silent' (transcribed, but nobody spoke), 'noaudio' (no voice was recorded),
 * 'recording' (session still in progress).
 */
export function transcriptState(session, words) {
  const st = session?.transcriptStatus;
  if (words.length && (st === 'completed' || !st)) return 'ready';
  if (st === 'completed' && session?.transcriptText) return 'text';
  // Finished with no words: the mic was on but nobody spoke. Without this the
  // session read as "arriving" forever because audioUrl is set.
  if (st === 'completed') return 'silent';
  if (st === 'failed') return 'failed';
  if (session?.audioUrl) return 'arriving';
  if (!session?.status || session.status === 'in_progress') return 'recording';
  return 'noaudio';
}

/** The secondary label beside the name: "Session #4". */
export function sessionNumberLabel(number) {
  return number ? `Session #${number}` : null;
}
